// WP-2.5 (M23 IN-1..5, S-07 `/inbox`): "nothing AI-touched is ever lost." Five status
// groups in IN-1's canonical order; a multi-select bulk bar (IN-3) for batch review; each
// row links to S-08's detail view.
import type { CaptureItemResponse, CaptureStatus } from '@pfos/shared';
import { formatMoney, type CurrencyCode } from '@pfos/engine';
import { Inbox as InboxIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { useBulkResolveInbox, useInbox } from '../lib/inbox';

const GROUPS: { status: CaptureStatus; label: string }[] = [
  { status: 'needs_one_tap', label: 'Needs one-tap' },
  { status: 'needs_question', label: 'Needs question' },
  { status: 'auto_recorded', label: 'Auto-recorded (24h)' },
  { status: 'duplicate_suspect', label: 'Duplicate-suspect' },
  { status: 'unparsed_failed', label: 'Unparsed' },
];

const SOURCE_GLYPH: Record<string, string> = { whatsapp: '🟢', web: '✍', import: '⇪' };
const AUTO_RECORDED_WINDOW_MS = 24 * 3_600_000;

function ageLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  return `${String(Math.floor(hours / 24))}d ago`;
}

function summaryLine(item: CaptureItemResponse): string | null {
  // WP-4.1 (AU-2): a grouped item has no single `extraction` — the count stands in for it.
  if (item.extractions !== null) return `${String(item.extractions.length)} linked events`;
  const ex = item.extraction;
  const amountMinor = ex?.amountMinor ?? null;
  const currency = ex?.currency ?? null;
  if (amountMinor === null || currency === null) return null;
  const bits = [formatMoney({ currency: currency as CurrencyCode, minorUnits: amountMinor })];
  if (ex?.category != null) bits.push(ex.category.label);
  if (ex?.account != null) bits.push(ex.account.label);
  return bits.join(' · ');
}

export function InboxPage(): React.JSX.Element {
  const { data, isLoading } = useInbox();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulk = useBulkResolveInbox();

  const grouped = useMemo(() => {
    const map = new Map<CaptureStatus, CaptureItemResponse[]>(GROUPS.map((g) => [g.status, []]));
    for (const item of data?.items ?? []) {
      if (item.status === 'auto_recorded') {
        const resolvedAgeMs =
          item.resolvedAt !== null ? Date.now() - Date.parse(item.resolvedAt) : Infinity;
        if (resolvedAgeMs > AUTO_RECORDED_WINDOW_MS) continue; // S-07: "Auto-recorded (24h)"
      }
      map.get(item.status)?.push(item);
    }
    return map;
  }, [data]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = (action: 'record' | 'reject'): void => {
    bulk.mutate(
      { ids: [...selected], action },
      {
        onSuccess: () => {
          setSelected(new Set());
        },
      },
    );
  };

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;

  const totalVisible = GROUPS.reduce((sum, g) => sum + (grouped.get(g.status)?.length ?? 0), 0);

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Inbox</h1>

      {totalVisible === 0 && (
        <EmptyState
          icon={InboxIcon}
          message="Nothing here — voice notes, WhatsApp messages, and imports that need your attention land here."
        />
      )}

      {selected.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-[var(--radius-card)] border border-border bg-surface px-4 py-2">
          <span className="text-sm text-ink">{selected.size} selected</span>
          <div className="flex gap-3">
            <button
              type="button"
              className="text-sm font-medium text-primary hover:underline"
              onClick={() => {
                runBulk('record');
              }}
            >
              Confirm all
            </button>
            <button
              type="button"
              className="text-sm font-medium text-danger hover:underline"
              onClick={() => {
                runBulk('reject');
              }}
            >
              Reject all
            </button>
          </div>
        </div>
      )}

      {GROUPS.map((g) => {
        const items = grouped.get(g.status) ?? [];
        if (items.length === 0) return null;
        return (
          <section key={g.status} className="mb-5">
            <h2 className="mb-2 text-sm font-medium text-ink">
              {g.label} <span className="text-xs text-muted">({items.length})</span>
            </h2>
            <ul className="divide-y divide-border rounded-[var(--radius-card)] border border-border bg-surface">
              {items.map((item) => {
                const snippet = item.rawText ?? '(no text)';
                const summary = summaryLine(item);
                return (
                  <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${snippet}`}
                      checked={selected.has(item.id)}
                      onChange={() => {
                        toggle(item.id);
                      }}
                    />
                    <Link
                      to={`/inbox/${item.id}`}
                      className="flex min-w-0 flex-1 items-center gap-3"
                    >
                      <span aria-hidden="true">{SOURCE_GLYPH[item.source] ?? '✍'}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-ink">{snippet}</span>
                        {summary !== null && (
                          <span className="block text-xs text-muted">{summary}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-muted">
                        {ageLabel(item.createdAt)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
